import { useEffect } from 'react'
import { DATEPICKER_FORMAT } from 'constants/dates'
import { sortBy } from 'lodash-es'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { ISelectOption } from '$/types'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

interface IHCAMCAToolbar {
  selectedLines: ISelectOption[]
  assessmentDateRange: Array<Date | null>
  selectedClassification: ISelectOption[]
  lineOptions: ISelectOption[]
  classificationOptions: ISelectOption[]
  setSelectedLines: (value: ISelectOption[]) => void
  setAssessmentDateRange: (value: Array<Date | null>) => void
  setSelectedClassification: (value: ISelectOption[]) => void
}

interface ICustomDateSelector {
  type: 'FROM' | 'TO'
  assessmentDateRange: Array<Date | null>
  setAssessmentDateRange: IHCAMCAToolbar['setAssessmentDateRange']
}

const CustomDateSelector = ({
  type,
  assessmentDateRange,
  setAssessmentDateRange
}: ICustomDateSelector) => {
  const indexOfCurrentDateType = type === 'FROM' ? 0 : 1

  return (
    <>
      <p className='my-auto ml-3 text-sm leading-5 text-gray-500'>{type}</p>

      <DatePicker
        format={DATEPICKER_FORMAT}
        value={assessmentDateRange[indexOfCurrentDateType]}
        onChange={(date: Date | null) => {
          const newAssessmentDateRange = [...assessmentDateRange]

          newAssessmentDateRange[indexOfCurrentDateType] = date
          setAssessmentDateRange(newAssessmentDateRange)
        }}
        minDate={
          type === 'TO' ? (assessmentDateRange[0] ?? undefined) : undefined
        }
        slotProps={{
          actionBar: {
            actions: ['clear'],
            sx: {
              justifyContent: 'flex-end',
              paddingRight: 3
            }
          },
          textField: {
            sx: {
              '& .MuiOutlinedInput-root:not(.Mui-focused) fieldset': {
                border: 'none'
              },
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderWidth: '1px'
              }
            }
          }
        }}
      />
    </>
  )
}

export const ToolbarContent = ({
  selectedLines,
  assessmentDateRange,
  selectedClassification,
  lineOptions,
  classificationOptions,
  setSelectedLines,
  setAssessmentDateRange,
  setSelectedClassification
}: IHCAMCAToolbar) => {
  useEffect(() => {
    setSelectedClassification([])
  }, [classificationOptions, setSelectedClassification])

  function selectedItemsCount(val: number | string): string {
    if (val === 0 || val === undefined) {
      return 'Select...'
    }

    return (val as string) + ' selected'
  }

  return (
    <div className='flex gap-x-4 border-b-4 bg-gray-100 px-6 pb-2 pt-4'>
      <div>
        <label className='font-semibold'>Date State History</label>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className='mt-2 flex gap-1 border border-gray-300 bg-white p-px'>
            <CustomDateSelector
              type='FROM'
              assessmentDateRange={assessmentDateRange}
              setAssessmentDateRange={setAssessmentDateRange}
            />
            <div className='my-auto ml-2 h-7 border-l-2 border-solid border-gray-500'></div>
            <CustomDateSelector
              type='TO'
              assessmentDateRange={assessmentDateRange}
              setAssessmentDateRange={setAssessmentDateRange}
            />
          </div>
        </LocalizationProvider>
      </div>
      <div className='w-1/4'>
        <label className='font-semibold'>Route</label>
        <SelectDropdownWrapper
          aria-label='Route'
          className='mt-2 w-full'
          mode='tags'
          options={sortBy(lineOptions, [
            (i) => i.label?.toLocaleString().toLowerCase(),
            (i) => i.label
          ]).map((option) => ({
            ...option,
            value: String(option.value)
          }))}
          value={selectedLines.map(({ value }) => String(value))}
          onChange={(_, selections) => {
            setSelectedLines(selections as ISelectOption[])
          }}
          placeholder={selectedItemsCount(selectedLines?.length)}
        />
      </div>
      <div className='w-1/4'>
        <label className='font-semibold'>Classification</label>
        <SelectDropdownWrapper
          aria-label='Classification'
          className='mt-2 w-full'
          mode='tags'
          options={classificationOptions.map((option) => ({
            ...option,
            value: String(option.value)
          }))}
          value={selectedClassification.map(({ value }) => String(value))}
          onChange={(_, selections) => {
            setSelectedClassification(selections as ISelectOption[])
          }}
          placeholder={selectedItemsCount(selectedClassification?.length)}
          tagGroupMaxWidth={180}
        />
      </div>
    </div>
  )
}

export default ToolbarContent
